import {
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useBrand } from '../brand/BrandProvider';
import { extractBrandFromAuthResponse } from '../utils/applyAuthBrand';
import '../styles/components/_login.scss';

const MagicLinkVerify: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setBrand } = useBrand();
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const hasVerifiedRef = useRef(false); // Prevent double calls in StrictMode

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasVerifiedRef.current) {
      return;
    }

    const verifyMagicLink = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('magicLink.missingToken'));
        return;
      }

      // Mark as verified immediately to prevent double calls
      hasVerifiedRef.current = true;

      try {
        const response = await authAPI.verifyMagicLink(token);
        
        // Store authentication data
        localStorage.setItem('student_token', response.token);
        localStorage.setItem('student_user', JSON.stringify(response.user));
        const brand = extractBrandFromAuthResponse(response);
        if (brand) {
          setBrand(brand);
        }

        setStatus('success');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } catch (err: any) {
        setStatus('error');
        const errorMsg = err.response?.data?.error || err.message || t('magicLink.verificationError');
        setErrorMessage(errorMsg);
      }
    };

    verifyMagicLink();
  }, [token, navigate, t]);

  return (
    <Box className="login">
      <Box className="login__left-panel">
        <Box className="login__content">
          {status === 'verifying' && (
            <>
              <CircularProgress size={60} sx={{ color: '#ffffff', mb: 3 }} />
              <Typography className="login__title">
                {t('magicLink.verifying')}
              </Typography>
              <Typography className="login__description">
                {t('magicLink.verifyingMessage')}
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Typography className="login__title" sx={{ color: '#4caf50' }}>
                {t('magicLink.success')}
              </Typography>
              <Typography className="login__description">
                {t('magicLink.successMessage')}
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <Typography className="login__title" sx={{ color: '#f44336' }}>
                {t('magicLink.error')}
              </Typography>
              <Typography className="login__description">
                {errorMessage}
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography 
                  className="login__description" 
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => navigate('/login', { replace: true })}
                >
                  {t('magicLink.backToLogin')}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box className="login__right-panel" />
    </Box>
  );
};

export default MagicLinkVerify;

